import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useReactFlow } from "reactflow";
import { saveGame } from "../../redux/asyncThunk/userActions";
import { stripSchemeTransient } from "./schemeLayout";

export function useDragParentGroup(parentGroupId) {
  const { getNode, getZoom, setNodes, getNodes, getEdges } = useReactFlow();
  const dispatch = useDispatch();
  const game = useSelector((state) => state.user?.game);

  return useCallback(
    (event) => {
      if (event.button !== 0) return;

      const parent = getNode(parentGroupId);
      if (!parent?.position) return;

      event.stopPropagation();
      event.preventDefault();

      const zoom = getZoom();
      const start = { x: event.clientX, y: event.clientY };
      const startPos = { ...parent.position };

      const onMove = (moveEvent) => {
        const dx = (moveEvent.clientX - start.x) / zoom;
        const dy = (moveEvent.clientY - start.y) / zoom;

        setNodes((nds) =>
          nds.map((node) =>
            node.id === parentGroupId
              ? {
                  ...node,
                  position: { x: startPos.x + dx, y: startPos.y + dy },
                }
              : node
          )
        );
      };

      const onUp = () => {
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);

        if (game?._id) {
          dispatch(
            saveGame({
              selectedGameId: game._id,
              nodes: stripSchemeTransient(getNodes()),
              edges: getEdges(),
            })
          );
        }
      };

      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    },
    [
      dispatch,
      game?._id,
      getEdges,
      getNode,
      getNodes,
      getZoom,
      parentGroupId,
      setNodes,
    ]
  );
}

export function useDragOrClick(parentGroupId, onClick, threshold = 6) {
  const startDrag = useDragParentGroup(parentGroupId);

  return useCallback(
    (event) => {
      if (event.button !== 0) return;

      const startX = event.clientX;
      const startY = event.clientY;
      let dragged = false;

      const onMove = (moveEvent) => {
        if (
          Math.hypot(moveEvent.clientX - startX, moveEvent.clientY - startY) >=
          threshold
        ) {
          dragged = true;
          document.removeEventListener("pointermove", onMove);
          document.removeEventListener("pointerup", onUp);
          startDrag(event);
        }
      };

      const onUp = () => {
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
        if (!dragged) onClick?.(event);
      };

      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    },
    [onClick, startDrag, threshold]
  );
}
